import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import EmptyState from '../components/EmptyState'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

interface TestCandidate {
  PK?: string
  candidate_id?: string
  phone?: string
  first_name?: string
  last_name?: string
  email?: string
  dni?: string
  distrito?: string
  promoter?: string
  owner?: string
  notes?: string
  contacted?: boolean
  created_at?: string
  created_by?: string
}

interface ListResponse {
  candidates: TestCandidate[]
  count: number
  next_key?: string
}

interface UploadResponse {
  imported: number
  phones: string[]
  errors: string[]
}

// Mismas columnas que acepta el backend (_parse_test_candidates)
const CSV_TEMPLATE = [
  'phone,first_name,last_name,email,dni,distrito,promoter,notes',
  '987654321,Juan,Pérez Quispe,juan@ejemplo.com,70123456,Miraflores,Promotor Test,Caso casado con separación de bienes',
  '987654322,Ana,Torres Ramos,ana@ejemplo.com,70123457,San Isidro,Promotor Test,Caso inmueble heredado',
  '987654323,Luis,Vega Soto,luis@ejemplo.com,70123458,Surco,Promotor Test,Caso con hipoteca activa',
].join('\n')

function fmtPhone(phone?: string) {
  if (!phone) return '—'
  const digits = phone.replace(/^51/, '')
  return `+51 ${digits.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`
}

function fmtDate(ts?: string) {
  if (!ts) return '—'
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

function candidateName(c: TestCandidate) {
  const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()
  return name || '—'
}

// ── Resultado de la carga ─────────────────────────────────────────────────────

function UploadResult({ result, onDismiss }: { result: UploadResponse; onDismiss: () => void }) {
  const ok = result.imported > 0
  return (
    <div style={{
      background: ok ? 'var(--c-brand-light)' : 'var(--c-danger-light)',
      border: `1px solid ${ok ? 'var(--c-brand-border)' : 'var(--c-danger-border)'}`,
      borderRadius: '12px', padding: '14px 16px', fontSize: '12.5px',
      color: ok ? 'var(--c-brand-ink)' : 'var(--c-danger-ink)',
      display: 'flex', flexDirection: 'column', gap: '8px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
        <b>
          {ok
            ? `${result.imported} ${result.imported === 1 ? 'usuario cargado' : 'usuarios cargados'}`
            : 'No se cargó ningún usuario'}
        </b>
        <button onClick={onDismiss}
          style={{ all: 'unset', cursor: 'pointer', fontSize: '15px', opacity: 0.6, lineHeight: 1 }}>✕</button>
      </div>

      {result.errors.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <b style={{ fontSize: '11.5px' }}>Filas descartadas ({result.errors.length}):</b>
          <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: 1.6 }}>
            {result.errors.slice(0, 12).map((e, i) => <li key={i}>{e}</li>)}
            {result.errors.length > 12 && <li>… y {result.errors.length - 12} más</li>}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── Página ────────────────────────────────────────────────────────────────────

export default function TestData() {
  const { canSupervise, isAdmin } = useAuth()
  const fileRef = useRef<HTMLInputElement>(null)

  const [candidates, setCandidates] = useState<TestCandidate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const [csvText, setCsvText] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [result, setResult] = useState<UploadResponse | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  const [tab, setTab] = useState<'individual' | 'csv'>('individual')
  const [showMore, setShowMore] = useState(false)
  const [form, setForm] = useState<Record<string, string>>({
    phone: '', first_name: '', last_name: '', email: '', dni: '',
    distrito: '', promoter: '', notes: '',
  })

  const canUpload = canSupervise

  const fetchCandidates = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await apiClient.get<ListResponse>('/test-candidates?limit=100')
      setCandidates(r.data.candidates ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar usuarios de prueba')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void fetchCandidates() }, [fetchCandidates])

  const handleUpload = async () => {
    if (!csvText.trim()) return
    setUploading(true); setUploadError(null); setResult(null)
    try {
      const r = await apiClient.post<UploadResponse>('/test-candidates', {
        csv: csvText,
      })
      setResult(r.data)
      if (r.data.imported > 0) {
        setCsvText('')
        await fetchCandidates()
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error al cargar los usuarios')
    } finally { setUploading(false) }
  }

  const handleUploadOne = async () => {
    const phone = form.phone.replace(/\D/g, '').replace(/^51/, '')
    if (phone.length !== 9) {
      setUploadError('Teléfono inválido: deben ser 9 dígitos (sin +51).')
      return
    }
    setUploading(true); setUploadError(null); setResult(null)
    try {
      const payload = Object.fromEntries(
        Object.entries({ ...form, phone }).filter(([, v]) => v.trim())
      )
      const r = await apiClient.post<UploadResponse>('/test-candidates', {
        candidates: [payload],
      })
      setResult(r.data)
      if (r.data.imported > 0) {
        setForm({
          phone: '', first_name: '', last_name: '', email: '', dni: '',
          distrito: '', promoter: '', notes: '',
        })
        setShowMore(false)
        await fetchCandidates()
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error al cargar el usuario')
    } finally { setUploading(false) }
  }

  const handleFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => setCsvText(String(reader.result ?? ''))
    reader.onerror = () => setUploadError('No se pudo leer el archivo')
    reader.readAsText(file, 'utf-8')
  }

  const handleDelete = async (phone: string) => {
    if (!phone) return
    if (!window.confirm(`¿Eliminar el usuario de prueba ${fmtPhone(phone)}?`)) return
    setDeleting(phone)
    try {
      await apiClient.delete(`/test-candidates/${phone}`)
      await fetchCandidates()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar')
    } finally { setDeleting(null) }
  }

  const downloadTemplate = () => {
    // BOM para que Excel respete los acentos al abrir el CSV
    const blob = new Blob(['﻿' + CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'plantilla-usuarios-prueba.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return candidates
    const q = search.toLowerCase()
    return candidates.filter((c) =>
      [candidateName(c), c.phone, c.email, c.dni, c.distrito, c.notes]
        .some((v) => v?.toLowerCase().includes(q))
    )
  }, [candidates, search])

  const filasPegadas = csvText.trim() ? csvText.trim().split('\n').length - 1 : 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* Aviso */}
      <div style={{
        background: 'var(--c-warn-light)', border: '1px solid var(--c-warn-border)',
        borderRadius: '12px', padding: '12px 16px', fontSize: '12.5px', color: 'var(--c-warn)',
        lineHeight: 1.6,
      }}>
        Estos usuarios se guardan con la <b>misma clave</b> que los candidatos de HubSpot, así que
        el agente los reconoce por teléfono y los saluda por su nombre. Quedan marcados como{' '}
        <b>datos de prueba</b>: no aparecen en <i>Candidatos HubSpot</i> y la importación del CRM
        nunca los sobrescribe.
        <div style={{ marginTop: '6px' }}>
          Cargar aquí <b>no habilita ninguna conversación</b>: quién puede escribirle al
          agente y a quién puede contactar se gobierna en <b>Acceso del agente</b>.
        </div>
      </div>

      {/* Carga */}
      {canUpload ? (
        <div style={{
          background: 'white', border: '1px solid var(--c-surface-border)', borderRadius: '16px',
          padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px',
        }}>
          <div style={{ display: 'flex', gap: '6px', borderBottom: '1px solid var(--c-surface-border)' }}>
            {(['individual', 'csv'] as const).map((t) => {
              const active = tab === t
              return (
                <button
                  key={t}
                  onClick={() => { setTab(t); setUploadError(null); setResult(null) }}
                  style={{
                    all: 'unset', cursor: 'pointer', padding: '9px 16px', fontSize: '12.5px',
                    fontWeight: 700,
                    color: active ? 'var(--c-brand-ink)' : 'var(--c-ink-muted)',
                    borderBottom: active ? '2px solid var(--c-brand)' : '2px solid transparent',
                    marginBottom: '-1px',
                  }}
                >
                  {t === 'individual' ? '👤 Cargar uno' : '📄 CSV masivo'}
                </button>
              )
            })}
          </div>

          {tab === 'individual' ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11.5px', fontWeight: 600, color: 'var(--c-ink-soft)' }}>
                  Teléfono <span style={{ color: 'var(--c-danger)' }}>*</span>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="987654321"
                    className="input"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11.5px', fontWeight: 600, color: 'var(--c-ink-soft)' }}>
                  Nombre
                  <input
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    placeholder="Juan"
                    className="input"
                  />
                </label>
              </div>

              <button
                onClick={() => setShowMore(!showMore)}
                style={{
                  all: 'unset', cursor: 'pointer', fontSize: '11.5px', fontWeight: 700,
                  color: 'var(--c-info-ink)', alignSelf: 'flex-start',
                }}
              >
                {showMore ? '− Menos campos' : '+ Más campos'}
              </button>

              {showMore && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                  {[
                    { key: 'last_name', label: 'Apellidos', ph: 'Pérez Quispe' },
                    { key: 'dni', label: 'DNI', ph: '70123456', mono: true },
                    { key: 'email', label: 'Email', ph: 'juan@ejemplo.com' },
                    { key: 'distrito', label: 'Distrito', ph: 'Miraflores' },
                    { key: 'promoter', label: 'Promotor', ph: 'Promotor Test' },
                    { key: 'notes', label: 'Nota / caso', ph: 'Casado con separación de bienes' },
                  ].map(({ key, label, ph, mono }) => (
                    <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11.5px', fontWeight: 600, color: 'var(--c-ink-soft)' }}>
                      {label}
                      <input
                        value={form[key] ?? ''}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                        placeholder={ph}
                        className="input"
                        style={mono ? { fontFamily: "'IBM Plex Mono', monospace" } : undefined}
                      />
                    </label>
                  ))}
                </div>
              )}

              <div style={{ fontSize: '11.5px', color: 'var(--c-ink-muted)', lineHeight: 1.6 }}>
                Solo el <b>teléfono</b> es obligatorio (9 dígitos, sin +51). El resto es opcional.
              </div>

              {uploadError && <ErrorAlert message={uploadError} />}
              {result && <UploadResult result={result} onDismiss={() => setResult(null)} />}

              <button
                onClick={() => void handleUploadOne()}
                disabled={uploading || !form.phone.trim()}
                className="btn-primary self-start disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploading ? 'Cargando…' : 'Cargar usuario'}
              </button>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button onClick={downloadTemplate}
                  style={{
                    all: 'unset', cursor: 'pointer', fontSize: '11.5px', fontWeight: 700,
                    color: 'var(--c-info-ink)', background: 'var(--c-info-light)',
                    padding: '6px 12px', borderRadius: '8px',
                  }}>
                  ↓ Plantilla CSV
                </button>
                <button onClick={() => fileRef.current?.click()}
                  style={{
                    all: 'unset', cursor: 'pointer', fontSize: '11.5px', fontWeight: 700,
                    color: 'var(--c-ink-muted)', background: 'var(--c-surface-faint)',
                    padding: '6px 12px', borderRadius: '8px',
                  }}>
                  📄 Elegir archivo
                </button>
                <input
                  ref={fileRef} type="file" accept=".csv,.txt,text/csv" style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) handleFile(f)
                    e.target.value = ''
                  }}
                />
              </div>

              <textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={'Pega aquí el CSV (con fila de cabecera). Por ejemplo:\n\n' + CSV_TEMPLATE}
                rows={8}
                className="input resize-y"
                style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', lineHeight: 1.6 }}
              />

              <div style={{ fontSize: '11.5px', color: 'var(--c-ink-muted)', lineHeight: 1.6 }}>
                Columna obligatoria: <b>phone</b> (también acepta <b>celular</b> o <b>telefono</b>), 9 dígitos.
                Opcionales: first_name, last_name, email, dni, distrito, promoter, owner, origin,
                lead_status, estado_gestion, evaluacion, motivo_rechazo, notes. Separador coma,
                punto y coma o tabulador.
              </div>

              {uploadError && <ErrorAlert message={uploadError} />}
              {result && <UploadResult result={result} onDismiss={() => setResult(null)} />}

              <button
                onClick={() => void handleUpload()}
                disabled={uploading || !csvText.trim()}
                className="btn-primary self-start disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploading
                  ? 'Cargando…'
                  : filasPegadas > 0
                    ? `Cargar ${filasPegadas} ${filasPegadas === 1 ? 'usuario' : 'usuarios'}`
                    : 'Cargar usuarios'}
              </button>
            </>
          )}
        </div>
      ) : (
        <p style={{ fontSize: '12.5px', color: 'var(--c-ink-muted)' }}>
          Solo Operadores y Administradores pueden cargar datos de prueba.
        </p>
      )}

      {/* Listado */}
      {loading ? (
        <LoadingSpinner label="Cargando usuarios de prueba..." />
      ) : error ? (
        <ErrorAlert message={error} onRetry={fetchCandidates} />
      ) : (
        <>
          <input
            type="search" placeholder="Buscar por nombre, teléfono, DNI o nota..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="input" style={{ maxWidth: '420px' }}
          />

          {filtered.length === 0 ? (
            <EmptyState
              icon="🧪"
              title={search ? 'Sin resultados' : 'Sin usuarios de prueba'}
              description={search
                ? `No coincide con "${search}".`
                : 'Carga un CSV arriba para empezar a probar el agente.'}
            />
          ) : (
            <div style={{
              background: 'white', border: '1px solid var(--c-surface-border)',
              borderRadius: '16px', overflow: 'hidden',
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
                  <thead>
                    <tr style={{ background: 'var(--c-surface-subtle)' }}>
                      {['NOMBRE', 'TELÉFONO', 'DNI', 'EMAIL', 'DISTRITO', 'CASO / NOTA', 'CARGADO', ''].map((h) => (
                        <th key={h} style={{
                          textAlign: 'left', padding: '11px 14px', fontSize: '10.5px', fontWeight: 700,
                          letterSpacing: '0.04em', color: 'var(--c-ink-muted)', whiteSpace: 'nowrap',
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, i) => {
                      const phone = c.phone ?? ''
                      return (
                        <tr key={c.candidate_id ?? c.PK ?? String(i)}
                            style={{ borderTop: '1px solid var(--c-surface-faint)' }}>
                          <td style={{ padding: '12px 14px', fontSize: '12.5px', fontWeight: 600 }}>
                            {candidateName(c)}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '12.5px', whiteSpace: 'nowrap',
                                       fontFamily: "'IBM Plex Mono', monospace", color: 'var(--c-ink-muted)' }}>
                            {fmtPhone(phone)}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '12.5px',
                                       fontFamily: "'IBM Plex Mono', monospace", color: 'var(--c-ink-muted)' }}>
                            {c.dni || '—'}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '12.5px', color: 'var(--c-ink-muted)' }}>
                            {c.email || '—'}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '12.5px', color: 'var(--c-ink-muted)' }}>
                            {c.distrito || '—'}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '12px', color: 'var(--c-ink-muted)',
                                       maxWidth: '260px' }}>
                            {c.notes || '—'}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '11.5px', color: 'var(--c-ink-muted)',
                                       whiteSpace: 'nowrap' }}>
                            {fmtDate(c.created_at)}
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                            {isAdmin && (
                              <button
                                onClick={() => void handleDelete(phone)}
                                disabled={deleting === phone}
                                style={{
                                  all: 'unset', cursor: deleting === phone ? 'default' : 'pointer',
                                  fontSize: '11.5px', fontWeight: 700, color: 'var(--c-danger-ink)',
                                  background: 'var(--c-danger-light)', padding: '4px 10px',
                                  borderRadius: '8px', opacity: deleting === phone ? 0.5 : 1,
                                  whiteSpace: 'nowrap',
                                }}>
                                {deleting === phone ? '…' : 'Eliminar'}
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{
                borderTop: '1px solid var(--c-surface-faint)', padding: '10px 16px',
                fontSize: '11.5px', color: 'var(--c-ink-muted)', background: 'var(--c-surface-subtle)',
              }}>
                {filtered.length} de {candidates.length} usuarios de prueba
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
